import * as React from "react";
import { HoloShimmer } from "@/components/Holo";
import { cn } from "@/lib/utils";

export interface CodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  /** Fired for every keydown inside the editor, so the sheet can own its shortcuts. */
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  readOnly?: boolean;
  className?: string;
  "aria-label"?: string;
}

// CodeMirror and its HTML mode are only ever downloaded through this import, so the chunk never
// reaches a coach's session.
const LazyCodeMirror = React.lazy(async () => {
  const [{ default: CodeMirror, EditorView }, { html }] = await Promise.all([
    import("@uiw/react-codemirror"),
    import("@codemirror/lang-html"),
  ]);
  const extensions = [html(), EditorView.lineWrapping];
  const Editor: React.FC<CodeEditorProps> = ({ value, onChange, onKeyDown, readOnly, className, ...rest }) => (
    <div className={cn("flex min-h-0 flex-1 flex-col", className)} onKeyDown={onKeyDown}>
      <CodeMirror
        value={value}
        onChange={onChange}
        extensions={extensions}
        readOnly={readOnly}
        height="100%"
        className="min-h-0 flex-1 overflow-hidden rounded-lg border border-slate-200 text-[13px] [&_.cm-editor]:h-full [&_.cm-scroller]:overflow-auto"
        basicSetup={{ lineNumbers: true, foldGutter: true, highlightActiveLine: true }}
        aria-label={rest["aria-label"]}
      />
    </div>
  );
  return { default: Editor };
});

/** The Source sheet's editor: CodeMirror in HTML mode, loaded on first use. */
export const CodeEditor: React.FC<CodeEditorProps> = props => (
  <React.Suspense
    fallback={
      <div className={cn("flex min-h-0 flex-1 flex-col", props.className)} data-testid="code-editor-loading">
        <HoloShimmer aspectRatio="landscape_4_3" showLogo={false} className="w-full flex-1 rounded-lg" />
      </div>
    }
  >
    <LazyCodeMirror {...props} />
  </React.Suspense>
);
